import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Package, Plus, Pencil, Trash2, ShieldCheck, AlertTriangle, CheckCircle2, ArrowLeft, MapPin, ListChecks,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  material_type: 'flyer', name: '', printed: '', placed: '', reorder_threshold: '',
  last_print_date: '', supplier: '', cost: '', notes: '',
};

const MaterialForm = ({ open, onClose, initial, types, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(initial || {}), printed: String(initial?.printed ?? ''), placed: String(initial?.placed ?? ''), reorder_threshold: String(initial?.reorder_threshold ?? '') });
  }, [open, initial]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, printed: parseInt(form.printed, 10) || 0, placed: parseInt(form.placed, 10) || 0, reorder_threshold: parseInt(form.reorder_threshold, 10) || 0 };
      if (initial?.id) {
        await api.put(`/admin/materials/${initial.id}`, body);
        toast.success('Material updated');
      } else {
        await api.post('/admin/materials', body);
        toast.success('Material added to inventory');
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="material-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-[#1a2744]">{initial?.id ? 'Edit Material' : 'Add Material'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Material type *</label>
              <Select value={form.material_type} onValueChange={(v) => set('material_type', v)}>
                <SelectTrigger className="h-11" data-testid="material-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(types || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Name / batch label</label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Launch batch flyers" className="h-11" data-testid="material-form-name" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Printed</label>
              <Input type="number" min="0" value={form.printed} onChange={(e) => set('printed', e.target.value)} className="h-11" data-testid="material-form-printed" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Placed / used</label>
              <Input type="number" min="0" value={form.placed} onChange={(e) => set('placed', e.target.value)} className="h-11" data-testid="material-form-placed" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Reorder at</label>
              <Input type="number" min="0" value={form.reorder_threshold} onChange={(e) => set('reorder_threshold', e.target.value)} className="h-11" data-testid="material-form-threshold" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Last print date</label>
              <Input type="date" value={form.last_print_date} onChange={(e) => set('last_print_date', e.target.value)} className="h-11" data-testid="material-form-date" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Supplier / printer</label>
              <Input value={form.supplier} onChange={(e) => set('supplier', e.target.value)} placeholder="Print shop name" className="h-11" data-testid="material-form-supplier" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Cost (optional)</label>
            <Input value={form.cost} onChange={(e) => set('cost', e.target.value)} placeholder="e.g. $85" className="h-11" data-testid="material-form-cost" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Notes (private)</label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="e.g. Glossy stock, reorder takes 5 days" data-testid="material-form-notes" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={busy} className="h-11 flex-1 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="material-form-save-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} {initial?.id ? 'Save Changes' : 'Add Material'}
            </Button>
            <Button onClick={onClose} variant="outline" className="h-11 font-bold">Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QUICK_TYPES = [
  ['flyer', 'Flyers'], ['half-page-flyer', 'Half-page flyers'], ['window-poster', 'Window posters'],
  ['yard-sign', 'Yard signs'], ['business-card', 'Business cards'], ['wallet-card', 'Wallet cards'],
  ['qr-poster', 'QR posters'], ['custom', 'Custom materials'],
];
const QUICK_EMPTY = { printed: '', placed: '', reorder_threshold: '', supplier: '', cost: '', notes: '', name: '' };

const QuickSetup = ({ types, onSaved, onClose }) => {
  const [rows, setRows] = useState(() => Object.fromEntries(QUICK_TYPES.map(([k]) => [k, { ...QUICK_EMPTY }])));
  const [busy, setBusy] = useState(false);
  const set = (type, k, v) => setRows((r) => ({ ...r, [type]: { ...r[type], [k]: v } }));
  const rowHasData = (r) => Object.values(r).some((v) => String(v).trim() !== '');

  const saveAll = async () => {
    const filled = QUICK_TYPES.filter(([k]) => rowHasData(rows[k]));
    if (!filled.length) { toast.error('Enter counts for at least one material first'); return; }
    setBusy(true);
    let created = 0;
    try {
      for (const [type, label] of filled) {
        const r = rows[type];
        await api.post('/admin/materials', {
          material_type: type,
          name: r.name || (types?.[type] || label),
          printed: parseInt(r.printed, 10) || 0,
          placed: parseInt(r.placed, 10) || 0,
          reorder_threshold: parseInt(r.reorder_threshold, 10) || 0,
          supplier: r.supplier, cost: r.cost, notes: r.notes,
        });
        created += 1;
      }
      toast.success(`${created} material${created === 1 ? '' : 's'} added to inventory`);
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Saving materials failed'); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-[#C9A84C]/50 bg-[#C9A84C]/5 p-4 mb-4" data-testid="materials-quick-setup">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-serif font-bold text-[#1a2744]">Add Your Printed Material Counts</p>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 text-xs font-bold text-slate-500" data-testid="quick-setup-close-btn">Close</Button>
      </div>
      <p className="text-xs text-slate-600 mt-1">Enter what you've had printed so far — only rows with numbers or notes are saved. Private inventory only; nothing is ordered or displayed publicly.</p>
      <div className="space-y-2.5 mt-3">
        {QUICK_TYPES.map(([type, label]) => (
          <div key={type} className="rounded-lg bg-white border border-slate-200 p-3" data-testid={`quick-setup-row-${type}`}>
            <p className="text-xs font-bold text-[#1a2744] mb-2">{label}</p>
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" min="0" placeholder="Printed" value={rows[type].printed} onChange={(e) => set(type, 'printed', e.target.value)} className="h-10 text-sm" data-testid={`quick-printed-${type}`} />
              <Input type="number" min="0" placeholder="Placed" value={rows[type].placed} onChange={(e) => set(type, 'placed', e.target.value)} className="h-10 text-sm" data-testid={`quick-placed-${type}`} />
              <Input type="number" min="0" placeholder="Reorder at" value={rows[type].reorder_threshold} onChange={(e) => set(type, 'reorder_threshold', e.target.value)} className="h-10 text-sm" data-testid={`quick-threshold-${type}`} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <Input placeholder="Supplier / printer" value={rows[type].supplier} onChange={(e) => set(type, 'supplier', e.target.value)} className="h-10 text-sm" data-testid={`quick-supplier-${type}`} />
              <Input placeholder="Cost (optional)" value={rows[type].cost} onChange={(e) => set(type, 'cost', e.target.value)} className="h-10 text-sm" data-testid={`quick-cost-${type}`} />
              <Input placeholder={type === 'custom' ? 'Material name' : 'Notes'} value={type === 'custom' ? rows[type].name : rows[type].notes} onChange={(e) => set(type, type === 'custom' ? 'name' : 'notes', e.target.value)} className="h-10 text-sm" data-testid={`quick-notes-${type}`} />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={saveAll} disabled={busy} className="h-11 mt-3 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="quick-setup-save-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Save Material Counts
      </Button>
    </div>
  );
};

export default function AdminMaterials() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const load = useCallback(() => {
    api.get('/admin/materials').then((r) => { setData(r.data); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}" from inventory?`)) return;
    try { await api.delete(`/admin/materials/${m.id}`); toast.success('Material deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex items-center justify-between gap-3" data-testid="materials-error">
        <p className="text-sm font-semibold text-red-800">Couldn't load the material inventory.</p>
        <Button onClick={load} variant="outline" size="sm" className="h-9 font-bold">Retry</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-materials">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-serif font-black text-2xl text-[#1a2744] flex items-center gap-2"><Package className="h-6 w-6 text-[#C9A84C]" /> Material Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">Track printed campaign materials — what's printed, placed, remaining, and when to reorder.</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="materials-back-launch-btn">
            <Link to="/admin/launch-content"><ArrowLeft className="h-4 w-4 mr-1.5" /> Launch Content</Link>
          </Button>
          <Button asChild variant="outline" className="h-11 font-bold btn-press" data-testid="materials-poster-drops-btn">
            <Link to="/admin/poster-drops"><MapPin className="h-4 w-4 mr-1.5" /> Poster Drop List</Link>
          </Button>
          <Button onClick={() => setQuickOpen((v) => !v)} variant="outline" className="h-11 font-bold btn-press border-[#C9A84C]/60 text-[#7A5C12]" data-testid="materials-quick-setup-btn">
            <ListChecks className="h-4 w-4 mr-1.5" /> Quick Setup
          </Button>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="materials-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Material
          </Button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex gap-2 items-start mt-3 mb-4" data-testid="materials-privacy-note">
        <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{data.note}</p>
      </div>

      {data.low.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="materials-low-banner">
          <p className="font-serif font-bold text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Materials Running Low</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {data.low.map((m) => (
              <Badge key={m.id} variant="outline" className="text-xs font-bold bg-white border-amber-300 text-amber-900">
                {m.name}: {m.remaining} left (reorder at {m.reorder_threshold})
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-amber-800 mt-2">Nothing is ordered automatically — this is a private reminder only.</p>
        </div>
      )}

      {(quickOpen || data.items.length === 0) && (
        <QuickSetup types={data.types} onSaved={load} onClose={() => setQuickOpen(false)} />
      )}

      {data.items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center" data-testid="materials-empty">
          <Package className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-[#1a2744]">No materials tracked yet</p>
          <p className="text-xs text-slate-500 mt-1">Use the quick setup above to enter your printed counts, or add one material at a time.</p>
          <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="h-10 mt-3 bg-[#1a2744] text-white font-bold btn-press" data-testid="materials-empty-add-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Material
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="materials-list">
          {data.items.map((m) => (
            <div key={m.id} className={`rounded-xl border bg-white p-4 ${m.reorder_needed ? 'border-amber-300' : 'border-slate-200'}`} data-testid={`material-row-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="font-bold text-[#1a2744] text-sm">{m.name}</p>
                    <Badge variant="outline" className="text-[10px] font-bold border-slate-300 text-slate-600">{m.type_label}</Badge>
                    {m.reorder_needed ? (
                      <Badge variant="outline" className="text-[10px] font-bold bg-amber-50 text-amber-900 border-amber-300" data-testid={`material-reorder-badge-${m.id}`}>Reorder needed</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] font-bold bg-emerald-50 text-emerald-800 border-emerald-300">Stocked</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {m.last_print_date && `Last printed ${m.last_print_date}`}{m.supplier && ` · ${m.supplier}`}{m.cost && ` · ${m.cost}`}
                  </p>
                  {m.notes && <p className="text-[11px] text-slate-600 italic mt-0.5">"{m.notes}"</p>}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(m); setFormOpen(true); }} className="h-9 w-9 p-0" title="Edit" data-testid={`material-edit-btn-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(m)} className="h-9 w-9 p-0 text-red-600 border-red-200 hover:bg-red-50" title="Delete" data-testid={`material-delete-btn-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
                  <p className="font-black text-lg text-[#1a2744]">{m.printed}</p>
                  <p className="text-[10px] font-bold uppercase text-slate-500">Printed</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-2 text-center">
                  <p className="font-black text-lg text-[#1a2744]">{m.placed}</p>
                  <p className="text-[10px] font-bold uppercase text-slate-500">Placed</p>
                </div>
                <div className={`rounded-lg border p-2 text-center ${m.reorder_needed ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-100'}`}>
                  <p className={`font-black text-lg ${m.reorder_needed ? 'text-amber-900' : 'text-emerald-800'}`} data-testid={`material-remaining-${m.id}`}>{m.remaining}</p>
                  <p className="text-[10px] font-bold uppercase text-slate-500">Remaining</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <MaterialForm open={formOpen} onClose={() => setFormOpen(false)} initial={editing} types={data.types} onSaved={load} />
    </div>
  );
}
